<template>
  <div class="international-phone-input">
    <div v-if="enableInternationalPhone" class="wrapper">
      <BaseElSelect
        v-model="syncPhoneCountry"
        class="country-select"
        :disabled="disabledAll"
        filterable
        @change="onPhoneCountryChange"
      >
        <BaseElSelectOption
          v-for="item in phoneCountryOptions"
          :key="item.value"
          :label="item.label"
          :value="item.value"
        >
          {{ item.label }}
        </BaseElSelectOption>
      </BaseElSelect>
    </div>

    <BaseElInput
      v-model="syncPhone"
      :disabled="disabledAll"
      :placeholder="placeholderText"
      clearable
      @input="onPhoneInput"
    />
  </div>
</template>

<script>
import { useVModel } from '@vueuse/core'
import { computed, defineComponent } from 'vue'
import {
  DEFAULT_PHONE_COUNTRY,
  getPhoneCountryPlaceholderExamples,
  getPhoneCountryOptions,
} from '@/utils/internationalPhone'
import { i18n } from '@/plugins/i18n/i18n'

export default defineComponent({
  name: 'InternationalPhoneInput',
  props: {
    phoneCountry: {
      type: String,
      default: DEFAULT_PHONE_COUNTRY,
    },
    phone: {
      type: String,
      default: '',
    },
    enableInternationalPhone: {
      type: Boolean,
      default: false,
    },
    disabledAll: {
      type: Boolean,
      default: false,
    },
  },
  emits: ['update:phoneCountry', 'update:phone', 'phone-country-change', 'phone-input'],
  setup (props, { emit }) {
    const syncPhoneCountry = useVModel(props, 'phoneCountry', emit)
    const syncPhone = useVModel(props, 'phone', emit)
    const phoneCountryOptions = computed(() => getPhoneCountryOptions(i18n.locale))
    const placeholderText = computed(() => {
      if (!props.enableInternationalPhone) {
        return i18n.t('member.tab.memberInfo.enterPhonePlaceholder')
      }

      const [firstExample, secondExample] = getPhoneCountryPlaceholderExamples(syncPhoneCountry.value)
      const example = secondExample
        ? i18n.t('member.tab.memberInfo.phoneExampleOr', {
          first: firstExample,
          second: secondExample,
        })
        : firstExample

      return i18n.t('member.tab.memberInfo.enterLocalPhonePlaceholderWithExample', {
        example,
      })
    })

    const onPhoneCountryChange = (phoneCountry) => {
      emit('phone-country-change', phoneCountry)
    }

    const onPhoneInput = (phone) => {
      emit('phone-input', phone)
    }

    return {
      syncPhoneCountry,
      syncPhone,
      phoneCountryOptions,
      placeholderText,
      onPhoneCountryChange,
      onPhoneInput,
    }
  },
})
</script>

<style lang="postcss" scoped>
.international-phone-input {
  @apply flex gap-[10px] items-center w-[560px];
}

.wrapper {
  @apply flex-shrink-0;
}

.country-select {
  @apply w-[180px];
}

:deep(.el-select .el-input) {
  @apply w-[180px];
}
</style>
